import { fromEnum } from '../../modules/enums';
import equipmentStructure from '../../modules/equipmentStructure';
import {
  EquipmentsShipEditType,
  EquipmentsSupplierEditType,
  EquipmentStructureType,
  TreeShipViewItemType,
  TreeSupplierViewItemType,
  UserEquipmentsType,
} from '../../types/EquipmentTypes';
import { ShipEquipmentType } from '../../types/ShipTypes';

// converts the state of EquipmentTreeEdit (which has no structure) to structured data (based on equipmentStructure.ts)
export const convertShipTreeEditToTreeView = (
  equipments: EquipmentStructureType | Record<string, unknown>
): TreeShipViewItemType[] => {
  const fullConverted = equipmentStructure.map((category) => ({
    title: category.title,
    items: category.items.map((j) => ({
      title: j.title,
      ...equipments[`${category.title}|${j.title}`],
      items: j.items.map((k) => ({
        title: k.title,
        ...equipments[`${category.title}|${j.title}|${k.title}`],
      })),
    })),
  }));

  const filteredConverted = fullConverted
    .map((category) => ({
      title: category.title,
      items: category.items
        .map((j) => ({
          ...j,
          items: j.items.filter(
            (k) => `${category.title}|${j.title}|${k.title}` in equipments
          ),
        }))
        .filter(
          (j) => `${category.title}|${j.title}` in equipments || j.items.length
        ),
    }))
    .filter((category) => category.items.length);

  return filteredConverted;
};

// converts the state of EquipmentTreeEdit (which has no structure) to structured data (based on SupplierEditStructure.ts)
export const convertSupplierTreeEditToTreeView = (
  equipments: EquipmentsSupplierEditType | Record<string, unknown>
): TreeSupplierViewItemType[] => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const fullConverted: any = equipmentStructure.map((category) => ({
    title: category.title,
    items: category.items.map((j) => ({
      title: j.title,
      ...(equipments[`${category.title}|${j.title}`] as Record<
        string,
        unknown
      >),
      items: j.items.map((k) => ({
        title: k.title,
        ...(equipments[`${category.title}|${j.title}|${k.title}`] as Record<
          string,
          unknown
        >),
      })),
    })),
  }));

  const filteredConverted = fullConverted
    .map((category) => ({
      title: category.title,
      items: category.items
        .map((j) => ({
          ...j,
          items: j.items.filter(
            (k) => `${category.title}|${j.title}|${k.title}` in equipments
          ),
        }))
        .filter(
          (j) => `${category.title}|${j.title}` in equipments || j.items.length
        ),
    }))
    .filter((category) => category.items.length);

  return filteredConverted;
};

// gets the ship equipments as returned from the db and makes them readable by the ShipEdit tree
export const convertEquipmentsToShipEditTree = (
  equipments: ShipEquipmentType[]
): EquipmentsShipEditType | Record<string, unknown> =>
  equipments.reduce((acc, e) => {
    const key = `${fromEnum(e.category)}|${
      e.subcategory ? `${fromEnum(e.subcategory)}|` : ''
    }${fromEnum(e.title)}`;
    return {
      ...acc,
      [key]: {
        maker: e.maker,
        type: e.type,
        description: e.description,
        attachments: e.attachments || [],
      },
    };
  }, {});

// gets the equipments as returned from the db and makes them readable by the SupplierEdit tree
export const convertEquipmentsToSupplierEditTree = (
  equipments: UserEquipmentsType[]
): EquipmentsSupplierEditType | Record<string, unknown> =>
  equipments.reduce((acc, e) => {
    const key = `${fromEnum(e.category)}|${
      e.subcategory ? `${fromEnum(e.subcategory)}|` : ''
    }${fromEnum(e.title)}`;
    return {
      ...acc,
      [key]: {
        standardServices: e.standardServices,
        customServices: e.customServices,
        makers: e.makers,
      },
    };
  }, {});
